import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { RotateCcw, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Article } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
import { DailyBriefSignup } from "../DailyBriefSignup";
import { homeTrack } from "../shared";
import { ResumeTimeline, CuratedList, TimelineSkeleton } from "./ResumeTimeline";
import { WorthResuming, TrendingForYou } from "./RightRail";

/* Jump back in — locked Smart Resume architecture:
   resume timeline left · Worth Resuming + Trending For You right · Daily Brief below. */
export const JumpBackInSection = ({ fallback }: { fallback: Article[] }) => {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["jump-back-in"],
    queryFn: async () => (await api.get("/account/jump-back-in")).data,
    enabled: !!user,
    staleTime: 60_000,
  });
  const { data: guestTrend } = useQuery({
    queryKey: ["jump-guest-trending"],
    queryFn: async () => (await api.get("/trending/velocity", { params: { window: "24h", limit: 5 } })).data,
    enabled: !user,
    staleTime: 120_000,
  });

  const resume: any[] = user ? data?.resume || [] : [];
  const usedSlugs = new Set(resume.map((a) => a.slug));
  const curated = fallback.filter((a) => !usedSlugs.has(a.slug));
  const worth = user
    ? data?.worth_resuming
    : curated[4] ? { ...curated[4], reason: "Worth your time" } : null;
  const guestSignal = (series: number[] = []) => {
    const half = Math.floor(series.length / 2);
    const prev = series.slice(0, half).reduce((s, v) => s + v, 0);
    const rec = series.slice(half).reduce((s, v) => s + v, 0);
    return rec > prev ? "rising" : rec < prev ? "cooling" : "high_interest";
  };
  const trending: any[] = user
    ? data?.trending_for_you || []
    : (guestTrend?.items || [])
        .filter((t: any) => !curated.slice(0, 5).some((c) => c.slug === t.slug))
        .slice(0, 3).map((t: any) => ({ ...t, reason: "Trending", signal: guestSignal(t.series) }));

  const subscribed = user
    ? data?.daily_brief_subscribed === true
    : typeof window !== "undefined" && localStorage.getItem("nl_subscribed") === "1";

  return (
    <section data-testid="jump-back-in" className="mt-10 sm:mt-14" aria-labelledby="jump-back-in-title">
      <div className="relative rounded-[16px] border border-[#A955FF]/45 bg-black p-5 sm:p-6 overflow-hidden">
        <div aria-hidden className="absolute -top-28 -right-24 h-80 w-80 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(124,58,237,0.1), transparent 68%)" }} />
        <div className="relative flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <span aria-hidden className="flex h-9 w-9 shrink-0 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/10 items-center justify-center text-[#A78BFA]">
                <RotateCcw className="h-4 w-4" />
              </span>
              <h2 id="jump-back-in-title" className="font-heading font-black text-white text-3xl sm:text-4xl tracking-tight">
                Jump back in
              </h2>
            </div>
            <p className="text-gray-400 text-sm sm:text-[15px] mt-2.5" data-testid="jump-back-in-subtitle">
              {user ? "Smart resume picks up right where you left off." : "Continue exploring the onchain economy."}
            </p>
          </div>
          <Link to="/account" data-testid="jump-back-in-view-all"
            onClick={() => homeTrack("jump_back_in", "view_all")}
            className="group shrink-0 inline-flex items-center gap-1 text-[#22D3EE] text-[13px] font-bold mt-2.5 hover:text-white motion-safe:transition-colors">
            View all
            <ArrowRight className="h-3.5 w-3.5 motion-safe:transition-transform motion-safe:duration-200 group-hover:translate-x-0.5" aria-hidden />
          </Link>
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,70fr)_minmax(0,30fr)] gap-4 mt-5 items-stretch">
          <div className="min-w-0 lg:flex lg:flex-col">
            {user ? (
              isLoading ? <TimelineSkeleton />
              : resume.length ? <ResumeTimeline items={resume} />
              : (
                <div className="lg:h-full lg:flex lg:flex-col">
                  <p data-testid="jump-back-in-empty" className="text-[11px] text-[#929AAC] mb-2.5 pl-8 sm:pl-9">
                    Your reading history will appear here as you explore NewsLayer.
                  </p>
                  <CuratedList items={curated.slice(0, 4)} label="Start with these" />
                </div>
              )
            ) : (
              <div className="lg:h-full lg:flex lg:flex-col">
                <CuratedList items={curated.slice(0, 4)} label="Worth your time" />
                <p className="text-[11px] text-[#929AAC] mt-3 pl-8 sm:pl-9">
                  <Link to="/login" data-testid="jump-back-in-signin" className="text-[#C08BFF] hover:underline">Sign in</Link>
                  {" "}to pick up exactly where you left off, on any device.
                </p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-col gap-4 min-w-0 items-start lg:items-stretch">
            <WorthResuming item={worth} premiumUser={!!user?.is_premium} />
            <TrendingForYou items={trending} />
          </div>
        </div>
      </div>
      <DailyBriefSignup className="mt-4" subscribed={subscribed} />
    </section>
  );
};
